// Resolves which role-scoped variant of the Sales-bucket `salesIntake`
// mode the current user should land on by default.
//
// Per docs/products/turbo_sales.md §5 (the bucket-move spec), one mode
// renders three variants:
//   - Sales-rep view: in-person lead-capture / "submit as deal" flow.
//   - Ops-reviewer view: paperwork-check / accept-hold-cancel decision.
//   - Mfg-accepter view: accept-to-pipeline / assign-plant /
//     materialize-unit (today's `ManufacturingOrderIntakePanel` UI).
//
// The variant order below puts the carved sub-role tokens FIRST so a
// user with a specific carved role (e.g. `mfg_accepter` only) lands on
// the right variant. Umbrella tokens come second so today's behavior is
// preserved: a `manufacturing` user defaults to the Mfg-accepter view
// (the only variant that exists with content today); a `sales` user
// defaults to the Sales-rep view (a scaffold today, populated by a
// later slice per turbo_sales.md §11.4); a tenant `admin` / `owner`
// defaults to the Ops-reviewer view (a scaffold; the variant they need
// once they're doing the paperwork-check role).
//
// Multi-role users (small orgs where one person wears every hat) get
// the highest-specificity variant by default; the panel's variant
// switcher (header tabs) lets them flip to the others they pass.

import type { AuthContextValue } from "@/contexts/AuthContext";
import type { AnchorEntitlements } from "@/lib/entitlements/anchorsClient";
import { SALES_REP_ROLES } from "@/lib/roles/turboSalesRoles";
import { OPS_REVIEWER_ROLES } from "@/lib/roles/turboOpsRoles";
import { MFG_ACCEPTER_ROLES } from "@/lib/roles/turboManufacturingRoles";

export type SalesIntakeVariant = "sales_rep" | "ops_reviewer" | "mfg_accepter";

export const SALES_INTAKE_VARIANTS: readonly SalesIntakeVariant[] = [
  "sales_rep",
  "ops_reviewer",
  "mfg_accepter",
] as const;

/**
 * The variants the current user is allowed to render. Drives the
 * variant-switcher tab strip at the top of the panel. A user with one
 * accessible variant doesn't see the switcher; multi-variant users get
 * the toggle.
 */
export function accessibleSalesIntakeVariants(
  auth: AuthContextValue,
  anchors?: AnchorEntitlements | null,
): SalesIntakeVariant[] {
  const out: SalesIntakeVariant[] = [];
  if (canAccessSalesRepVariant(auth)) out.push("sales_rep");
  if (canAccessOpsReviewerVariant(auth)) out.push("ops_reviewer");
  if (canAccessMfgAccepterVariant(auth, anchors)) out.push("mfg_accepter");
  return out;
}

/**
 * Default variant for the current user. Specificity wins: a carved
 * sub-role token outranks its umbrella implication (e.g. a user with
 * `mfg_accepter` but no `manufacturing` still lands on the Mfg view;
 * a user with both `manufacturing` and `sales_rep` lands on Mfg
 * because that matches today's pre-move behavior for the legacy
 * `mfgOrderIntake` content).
 *
 * Fallback for platform escape-hatch users (platform_*) with no org
 * role tokens: Mfg view, since that's the only variant with content
 * today.
 */
export function defaultSalesIntakeVariant(
  auth: AuthContextValue,
  anchors?: AnchorEntitlements | null,
): SalesIntakeVariant {
  const mfgOk = mfgVariantAllowed(auth, anchors, "default");
  if (mfgOk && auth.hasRole("mfg_accepter", "manufacturing_order_processor")) return "mfg_accepter";
  if (auth.hasRole("ops_reviewer")) return "ops_reviewer";
  if (auth.hasRole("sales_rep")) return "sales_rep";
  if (mfgOk && auth.hasRole("manufacturing")) return "mfg_accepter";
  if (auth.hasRole("sales")) return "sales_rep";
  if (auth.hasRole("admin", "owner", "org_admin", "org_owner")) return "ops_reviewer";
  return mfgOk ? "mfg_accepter" : "ops_reviewer";
}

function canAccessSalesRepVariant(auth: AuthContextValue): boolean {
  return auth.hasRole(...SALES_REP_ROLES);
}

function canAccessOpsReviewerVariant(auth: AuthContextValue): boolean {
  return auth.hasRole(...OPS_REVIEWER_ROLES);
}

function canAccessMfgAccepterVariant(
  auth: AuthContextValue,
  anchors?: AnchorEntitlements | null,
): boolean {
  return mfgVariantAllowed(auth, anchors, "tab");
}

/**
 * Manufacturer view needs the Mfg role AND the org's manufacturing mode.
 * `anchors === undefined` keeps role-only unit tests unchanged.
 * While entitlements are loading (`null`), hide the tab when another
 * variant exists so a sales/ops org does not land on a dead Manufacturer panel.
 */
function mfgVariantAllowed(
  auth: AuthContextValue,
  anchors: AnchorEntitlements | null | undefined,
  purpose: "tab" | "default",
): boolean {
  if (!auth.hasRole(...MFG_ACCEPTER_ROLES)) return false;
  if (anchors === undefined) return true;
  if (anchors === null) {
    if (purpose === "default") return false;
    return !canAccessOpsReviewerVariant(auth) && !canAccessSalesRepVariant(auth);
  }
  return anchors.turbo_manufacturing === true;
}

export const SALES_INTAKE_VARIANT_LABELS: Record<SalesIntakeVariant, string> = {
  sales_rep: "Sales rep",
  ops_reviewer: "Ops review",
  mfg_accepter: "Manufacturer",
};
